import type { EasingName } from '../types';

export type Easing = (t: number) => number;

type TickerLike = {
  add(fn: (t: { deltaMS: number }) => void): unknown;
  remove(fn: (t: { deltaMS: number }) => void): unknown;
};

const backOutWith =
  (s: number): Easing =>
  (t) => {
    const x = t - 1;
    return x * x * ((s + 1) * x + s) + 1;
  };

/** Cubic-bezier easing (CSS semantics) solved numerically for x -> y. */
export function cubicBezier(x1: number, y1: number, x2: number, y2: number): Easing {
  const bx = (t: number) => 3 * x1 * (1 - t) * (1 - t) * t + 3 * x2 * (1 - t) * t * t + t * t * t;
  const by = (t: number) => 3 * y1 * (1 - t) * (1 - t) * t + 3 * y2 * (1 - t) * t * t + t * t * t;
  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let lo = 0;
    let hi = 1;
    let t = x;
    for (let i = 0; i < 24; i++) {
      const cx = bx(t);
      if (Math.abs(cx - x) < 1e-5) break;
      if (cx < x) lo = t;
      else hi = t;
      t = (lo + hi) / 2;
    }
    return by(t);
  };
}

export const Easings: Record<EasingName, Easing> & { backOutWith(overshoot: number): Easing } = {
  linear: (t) => t,
  quadIn: (t) => t * t,
  quadOut: (t) => 1 - (1 - t) * (1 - t),
  quadInOut: (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2),
  cubicOut: (t) => 1 - Math.pow(1 - t, 3),
  backOut: backOutWith(1.70158),
  elasticOut: (t) => {
    if (t === 0 || t === 1) return t;
    return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * ((2 * Math.PI) / 3)) + 1;
  },
  bounceOut: (t) => {
    const n1 = 7.5625;
    const d1 = 2.75;
    if (t < 1 / d1) return n1 * t * t;
    if (t < 2 / d1) return n1 * (t -= 1.5 / d1) * t + 0.75;
    if (t < 2.5 / d1) return n1 * (t -= 2.25 / d1) * t + 0.9375;
    return n1 * (t -= 2.625 / d1) * t + 0.984375;
  },
  softOut: cubicBezier(0.16, 0.84, 0.34, 1),
  bounceCard: cubicBezier(0.16, 1.35, 0.38, 1),
  backOutWith,
};

interface Active {
  elapsed: number;
  delay: number;
  duration: number;
  easing: Easing;
  update: (t: number, deltaMS: number) => void;
  resolve: () => void;
  loop: boolean;
  period: number;
}

/**
 * A group of tweens driven by one ticker. skip() finishes every active tween instantly (update(1)),
 * cancel() drops them without completing. Both resolve pending promises so callers never hang.
 */
export class Timeline {
  private items = new Set<Active>();
  private readonly ticker: TickerLike;
  private readonly tick = (t: { deltaMS: number }) => this.step(t.deltaMS);
  private attached = false;
  private destroyed = false;

  constructor(ticker: TickerLike) {
    this.ticker = ticker;
  }

  private attach(): void {
    if (!this.attached && !this.destroyed) {
      this.ticker.add(this.tick);
      this.attached = true;
    }
  }

  private detach(): void {
    if (this.attached) {
      this.ticker.remove(this.tick);
      this.attached = false;
    }
  }

  private step(deltaMS: number): void {
    for (const it of [...this.items]) {
      it.elapsed += deltaMS;
      if (it.elapsed < it.delay) continue;
      if (it.loop) {
        const p = ((it.elapsed - it.delay) % it.period) / it.period;
        it.update(p, deltaMS);
        continue;
      }
      const raw = it.duration <= 0 ? 1 : Math.min(1, (it.elapsed - it.delay) / it.duration);
      it.update(it.easing(raw), deltaMS);
      if (raw >= 1) {
        this.items.delete(it);
        it.resolve();
      }
    }
    if (this.items.size === 0) this.detach();
  }

  tween(o: { duration: number; easing?: Easing; delay?: number; update: (t: number) => void }): Promise<void> {
    if (this.destroyed) return Promise.resolve();
    return new Promise<void>((resolve) => {
      this.items.add({
        elapsed: 0,
        delay: o.delay ?? 0,
        duration: o.duration,
        easing: o.easing ?? Easings.linear,
        update: o.update,
        resolve,
        loop: false,
        period: 0,
      });
      this.attach();
    });
  }

  wait(ms: number): Promise<void> {
    return this.tween({ duration: ms, update: () => {} });
  }

  /** Repeats update(phase 0..1) every `period` ms until the returned function is called. */
  loop(o: { period: number; update: (t: number, deltaMS: number) => void }): () => void {
    if (this.destroyed) return () => {};
    const it: Active = {
      elapsed: 0,
      delay: 0,
      duration: 0,
      easing: Easings.linear,
      update: o.update,
      resolve: () => {},
      loop: true,
      period: Math.max(1, o.period),
    };
    this.items.add(it);
    this.attach();
    return () => {
      this.items.delete(it);
      if (this.items.size === 0) this.detach();
    };
  }

  /** Jump every tween to its end (update(1)) and resolve; loops are stopped. */
  skip(): void {
    const items = [...this.items];
    this.items.clear();
    this.detach();
    for (const it of items) {
      if (it.loop) continue;
      try {
        it.update(1, 0);
      } finally {
        it.resolve();
      }
    }
  }

  /** Drop everything without completing (promises still resolve so awaiting code continues). */
  cancel(): void {
    const items = [...this.items];
    this.items.clear();
    this.detach();
    for (const it of items) if (!it.loop) it.resolve();
  }

  get active(): boolean {
    return this.items.size > 0;
  }

  destroy(): void {
    this.cancel();
    this.destroyed = true;
  }
}
